import React from 'react';
import { Box, Typography, Alert, List, ListItem, ListItemText, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/** Loksy Citizen + Mitra marketplace terms (Loksy brand only). */
export const LOCSY_TERMS_SECTIONS = [
  {
    title: '1. Acceptance of terms',
    bullets: [
      'By registering for, accessing, or using Loksy Citizen, Loksy Mitra, or related Loksy web services (“Loksy”, “we”, “us”), you agree to these Terms & Conditions, the Privacy Policy, Community Guidelines, Platform Disclaimer, and Refund & Cancellation Policy.',
      'If you do not agree, do not create an account or continue using the apps.',
      'You must provide accurate registration details and keep your PIN, password, and device access secure.',
    ],
  },
  {
    title: '2. What Loksy is (intermediary platform)',
    bullets: [
      'Loksy operates as an online intermediary and technology marketplace under the Information Technology Act, 2000 and the Intermediary Guidelines (as amended).',
      'Loksy connects Citizens and Mitras (local businesses / service points). It does not create, author, or officially certify most listings, ads, job alerts, coupons, reviews, pamphlets, or citizen posts.',
      'A listing or verification badge on Loksy is not a government licence, quality certification, or endorsement.',
      'Subject to applicable law (including Section 79 safe-harbour conditions and due diligence), Loksy is not liable for content published by Mitras, Citizens, or other third parties.',
    ],
  },
  {
    title: '3. Accounts & eligibility',
    bullets: [
      'You must be 18 years or older (or otherwise legally able to enter a binding agreement under Indian law) to use Loksy. Mitra registrants must be authorised for the business they list.',
      'One person / business should not create accounts to evade bans, scrape data, or spam.',
      'Loksy may suspend, restrict, or terminate accounts for policy breach, fraud risk, legal requests, or court / government orders.',
    ],
  },
  {
    title: '3A. Admin-assisted Vyapar registration',
    bullets: [
      'If merchant OTP is unavailable, an authorised Loksy admin or specifically permitted operator may register an adult merchant only after explaining the notice and obtaining written or signed authority for account creation and public business listing. The merchant must be authorised to act for the business.',
      'The record includes the operator identity, time, notice version and authorization reference. Phone ownership is not OTP verified and this process does not grant a Verified badge, authorise marketing, automatic payments, or waive statutory rights.',
      'The merchant receives a registration receipt and controls their account through their phone and PIN. They should change a PIN known to another person. Existing accounts cannot be replaced using assisted registration.',
      'Request a copy/correction of the registration, withdraw consent, report unauthorized registration or request deletion through Help & Support or support@knls.in. We may request proportionate evidence of authority to protect the account; necessary records may be retained for disputes or legal duties. Operators must retain authorization securely and only as long as necessary for these purposes.',
      'These declarations do not exclude Loksy obligations or liability imposed by applicable law.',
    ],
  },
  {
    title: '4. User-generated & Mitra-published content',
    bullets: [
      'Business profiles, offers, products, services, prices, pamphlets, and contact details are published by the respective Mitra / business.',
      'Citizen posts (marketplace, rentals, jobs, services, updates) are published by the posting user.',
      'You remain solely responsible for content you upload, publish, or share, and for verifying information before you pay, visit, apply, or rely on it.',
      'Job alerts and similar notices may be informational only and are not authenticated government or employer notifications unless expressly labelled otherwise.',
    ],
  },
  {
    title: '5. Coupons, DigiDrop & offers',
    bullets: [
      'Coupons unlocked via DigiDrop or allotted in-app are offers from Mitras — not a Loksy payment or redemption guarantee.',
      'At billing, applying a coupon depends fully on the Mitra’s willingness. The Mitra may retrieve & apply, edit, or refuse the coupon. Loksy cannot force honouring.',
      'Admin or Mitra may pause templates, remove Share with Loksy, or delete issued coupons without prior notice.',
      'Disputes about coupon value, service quality, or refusal are primarily between the Citizen and the Mitra.',
    ],
  },
  {
    title: '6. Marketplace & prohibited content',
    bullets: [
      'Publish only lawful, accurate, and permissioned content. Legal second-hand items, house/shop rentals, and real-estate sales may be allowed subject to Loksy admin review.',
      'Prohibited: illegal goods/services, controlled substances, weapons, stolen goods, counterfeit documents/currency, fake jobs, phishing, fraud, hate, obscenity, child exploitation, or IP / privacy infringement.',
      'Violations may lead to removal, suspension, and legal action under the IT Act, Bharatiya Nyaya Sanhita, and other applicable laws.',
    ],
  },
  {
    title: '7. Payments, Membership Plans & disputes',
    bullets: [
      'Shop UPI: where you pay a Mitra via UPI, settlement is between Citizen and Mitra. Loksy does not hold those funds.',
      'Membership Plans: paid Mitra platform features are sold as Membership Plans via Razorpay. Loksy Coins are not an active paid product.',
      'See the Refund & Cancellation Policy for cancel, renew, downgrade, and refund rules.',
      'Loksy is not responsible for UPI delays, bank-side failures, pending transactions, or merchant refusal of payment methods, except where Loksy is merchant of record for Membership.',
    ],
  },
  {
    title: '8. Moderation, reports & takedown',
    bullets: [
      'Loksy may review, edit visibility, unlist, suspend, or remove content or accounts for policy, safety, legal requests, or court / government orders.',
      'Report unlawful or harmful content to support@knls.in with post / shop details. Loksy aims to acknowledge grievances within 72 hours where practicable and will act as required under applicable law and platform policy.',
    ],
  },
  {
    title: '9. Intellectual property',
    bullets: [
      'Loksy apps, branding, software, and platform design remain the property of Loksy or its licensors.',
      'You grant Loksy a non-exclusive licence to host and display content you publish as needed to operate the service.',
      'Do not copy, scrape, reverse engineer, or misuse Loksy systems or other users’ content without permission.',
    ],
  },
  {
    title: '10. Service changes',
    bullets: [
      'Platform features (directory, updates, coupons, postings, Membership Plans, payments) may change, pause, or end at any time.',
      'Continued use after material updates constitutes acceptance where permitted by law. We may bump in-app acceptance versions when terms change.',
    ],
  },
  {
    title: '11. Limitation of liability',
    bullets: [
      'To the fullest extent permitted by law, Loksy, its affiliates, and officers are not liable for losses arising from inaccurate, outdated, misleading, or unlawful third-party content; failed transactions between users; refused coupons; or decisions you make based on app information.',
      'Nothing in these terms excludes liability that cannot be excluded under applicable Indian consumer or other mandatory law.',
    ],
  },
  {
    title: '12. Governing law, grievance & contact',
    bullets: [
      'Loksy principal place of business for these services: Chintalapudi – 534460, Eluru District, Andhra Pradesh, India.',
      'GSTIN / CIN: TODO_LEGAL_ENTITY (to be published when confirmed).',
      'Grievance Officer (interim contact): Loksy Support — support@knls.in · +91 9885750476 / +91 7815970729. Name/designation will be published when appointed. Aim: acknowledge within 72 hours where practicable.',
      'These terms are governed by the laws of India and the State of Andhra Pradesh, without prejudice to any mandatory consumer protections that cannot be waived.',
      'Subject to applicable law, courts having territorial jurisdiction over Chintalapudi and/or Eluru District, Andhra Pradesh, shall have jurisdiction over disputes arising from these terms or your use of Loksy.',
    ],
  },
];

/**
 * @param {{ dense?: boolean, showWarning?: boolean }} props
 */
export default function LocsyTermsSection({ dense = false, showWarning = true }) {
  const spacing = dense ? 1.5 : 2;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {showWarning && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loksy is a technology marketplace. Ads, shop info, jobs, coupons, and posts are mostly published by
          Mitras or Citizens — not as Loksy official government or employer notices. Always verify before you act.
          Paid Mitra features use Membership Plans (not coins).
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" paragraph>
        Welcome to Loksy. By registering or using Loksy Citizen / Loksy Mitra, you agree to the following terms
        and conditions. Also read the{' '}
        <Link component={RouterLink} to="/refund-policy">
          Refund & Cancellation Policy
        </Link>
        ,{' '}
        <Link component={RouterLink} to="/privacy-policy">
          Privacy Policy
        </Link>
        , and{' '}
        <Link component={RouterLink} to="/disclaimer">
          Platform Disclaimer
        </Link>
        . Last updated: 12 September 2026.
      </Typography>

      {LOCSY_TERMS_SECTIONS.map((section) => (
        <Box key={section.title} sx={{ mb: spacing }}>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700, fontSize: dense ? '1rem' : undefined }}
          >
            {section.title}
          </Typography>
          <List dense={dense} disablePadding>
            {section.bullets.map((line) => (
              <ListItem key={line} alignItems="flex-start" sx={{ py: 0.25, pl: 0 }}>
                <ListItemText
                  primaryTypographyProps={{ variant: dense ? 'body2' : 'body1' }}
                  primary={<span>• {line}</span>}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ mt: 2 }} />
        </Box>
      ))}
    </Box>
  );
}
